/**
 * Where the month in progress will close, from what has actually happened —
 * not from the plan's daily ramp, which is what the re-forecast paces on.
 *
 * The same method the CFO's snapshot uses, made explicit:
 *
 *   new business   month to date + (average of the last full days) × days left
 *   renewals       month to date + renewals still SCHEDULED this month, from
 *                  the subscription calendar. A run-rate understates them: the
 *                  first days of a month can hold few due dates, and the
 *                  schedule knows exactly when the rest fall.
 *
 * "Days left" is fractional: today's part-day has its actuals in month to date
 * and only the rest of today is projected.
 */

const sum = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0)

/**
 * @param {object} month     getDailyActuals payload: { days_in_month, days_elapsed, days: [...] }
 * @param {object[]} recent  full days for the run-rate (Daily Report rows, as month.days)
 * @param {number|null} scheduledRenewals  renewal dollars still due this month, or null if unknown
 */
export function projectMonth(month, recent, scheduledRenewals) {
  if (!month || !month.days_in_month) return null
  const left = Math.max(0, month.days_in_month - (month.days_elapsed || 0))
  const days = month.days || []
  const n = recent?.length || 0
  const rate = key => (n ? sum(recent, key) / n : null)

  const line = key => {
    const toDate = sum(days, key)
    const perDay = rate(key)
    return { toDate, perDay, projected: perDay == null ? null : toDate + perDay * left }
  }

  const customers = line('new_customers')
  const newRevenue = line('new_revenue')
  const spendKnown = days.every(d => d.ad_spend != null) && (recent || []).every(d => d.ad_spend != null)
  const spend = spendKnown ? line('ad_spend') : { toDate: null, perDay: null, projected: null }

  const renewalsToDate = sum(days, 'recurring_revenue') + sum(days, 'unattributed_revenue')
  const renewals = {
    toDate: renewalsToDate,
    scheduled: scheduledRenewals,
    projected: scheduledRenewals == null ? null : renewalsToDate + scheduledRenewals,
  }

  const revenue = {
    toDate: sum(days, 'total_revenue'),
    projected: newRevenue.projected == null || renewals.projected == null ? null : newRevenue.projected + renewals.projected,
  }

  const cac = {
    toDate: customers.toDate > 0 && spend.toDate != null ? spend.toDate / customers.toDate : null,
    projected: customers.projected > 0 && spend.projected != null ? spend.projected / customers.projected : null,
  }

  return { daysLeft: left, daysInMonth: month.days_in_month, runRateDays: n, customers, newRevenue, renewals, revenue, spend, cac }
}
